import { AiOutlineExclamationCircle } from 'react-icons/ai'
import { useForm } from 'react-hook-form'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { WarningModal } from 'components/Modals/WarningModal'
import NotificationActions from 'actions/Notification'
import { useDispatch, useSelector } from 'react-redux'
import NotificationService from 'services/Notification'
import { useCallbackPrompt } from 'hooks/useCallbackPrompt'
import { Beforeunload } from 'react-beforeunload'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
    BackButton,
    ContainerForm,
    MainForm,
    Top,
    TopInputs,
    SaveButton,
    TitleForm,
    SubtitleForm
} from '../GroupActionPlan/styles'
import { Breadcrumbs, SecondTextBreadcrumbs } from '../../NotificationsTreatmentsSteps/Header/styles'
import { IAuthenticationReducer } from 'types/autentication'
import { RootReducerState } from 'types/global'
import { NotificationAction, NotificationReducer } from 'types/notification'
import { Dispatch } from 'redux'
import { GroupData } from '../GroupActionPlan/helpers'
import {
    buildLondonProtocolSysIdByNotificationMap,
    getGroupNotificationSysId,
    mergeFirstLondonProtocolIdFromRedux,
    partitionNotificationsByProtocolMap
} from './helpers'
import { setStartedDataV3 } from '../../NotificationsTreatmentsSteps/LondonProtocol/helper'
import { LondonProtocolFormFieldsBodyV3 } from '../../NotificationsTreatmentsSteps/LondonProtocol/Version/Form/LondonProtocolFormFieldsBodyV3'
import VariableActions from 'actions/Variable'
import { VariableAction, VariableReducer } from 'types/variable'
import { getVarible } from 'utils/variblesHandler'
import DefaultModal from 'components/Modals'
import DataLondonProtocolInfoModal from 'pages/NotifyEvent/components/modals/DataLondonProtocolInfoModal'
import { listOfRoles } from 'components/WithRoleAuthorization/WithRoleAuthorization'

export const GroupLondonProtocolForm = () => {
    const LONDON_PROTOCOL_VERSION = 'v3'
    const { id } = useParams()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const notificationDispatch = dispatch as Dispatch<NotificationAction>
    const variableDispatch = dispatch as Dispatch<VariableAction>

    const [isSaving, setIsSaving] = useState(false)
    const [groupData, setGroupData] = useState<GroupData | null>(null)
    const [showModal, setShowModal] = useState(false)
    const [manualDirty, setManualDirty] = useState(false)
    const [typesOfDiagnosis, setTypesOfDiagnosis] = useState<any[]>([])
    const [omsClassifications, setOmsClassifications] = useState<any[]>([])

    const auth: IAuthenticationReducer = useSelector((state: RootReducerState) => state.authenticationReducers)
    const notificationSelector: NotificationReducer = useSelector((state: RootReducerState) => state.notificationsReducer)
    const variableSelector: VariableReducer = useSelector((state: RootReducerState) => state.variableReducers)

    const notification = useMemo(() => notificationSelector?.notificationById, [notificationSelector?.notificationById])
    const protocoloLondon = useMemo(() => notificationSelector?.londonProtocol, [notificationSelector?.londonProtocol])

    const protocoloNotificationFactors = useMemo(() => {
        const keys = Object.keys(notificationSelector?.factors || {})
        const response: Record<string, any[]> = {}
        keys.forEach((k) => {
            response[k] =
                notificationSelector?.factors?.[k]?.filter(
                    (f) => f?.u_category_factors_version === LONDON_PROTOCOL_VERSION
                ) || []
        })
        return response
    }, [notificationSelector?.factors, notificationSelector?.notificationById?.u_version?.value])

    const londonVaribles = useMemo(() => notificationSelector?.londonVaribles, [notificationSelector?.londonVaribles])

    const organizationalConsequences = useMemo(() => {
        const oc = londonVaribles?.organizational_consequences
        if (!oc?.length) return []
        return oc.slice(5, 6).concat(oc.slice(0, 5).concat(oc.slice(6)))
    }, [londonVaribles?.organizational_consequences])

    const currentUser = useMemo(() => auth.dataJwt, [auth])

    const onlyProfilecanInputNSPOpinion = useMemo(() => {
        return [listOfRoles.admin, listOfRoles.location_qa].includes(currentUser?.profile)
    }, [currentUser])

    const groupKeyDisplay = useMemo(() => {
        const fromInfo = groupData?.group_info?.u_number?.display_value?.trim()
        const fromKey = typeof groupData?.group_key === 'string' ? groupData.group_key.trim() : ''
        return fromInfo || fromKey || ''
    }, [groupData])

    const {
        register,
        control,
        watch,
        setValue,
        getValues,
        handleSubmit,
        formState: { errors, dirtyFields }
    } = useForm()

    const chronologicalReportValue: string = watch('chronological_report') || ''

    const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
        (!!Object.keys(dirtyFields).length || manualDirty) && !isSaving
    )

    useEffect(() => {
        const fetchGroupData = async () => {
            if (!id) return
            try {
                const groupResponse = await NotificationService.getGroupDataById(id)
                setGroupData(groupResponse)
            } catch (error) {
                console.error('Erro ao buscar dados do grupo:', error)
            }
        }

        fetchGroupData()
    }, [id])

    useEffect(() => {
        VariableActions.getAllVariables(variableDispatch)
    }, [variableDispatch])

    useEffect(() => {
        const firstNotificationId = groupData?.notifications?.[0]?.sys_id?.value
        if (!firstNotificationId) return

        NotificationActions.getNotificationLondonVaribles(notificationDispatch)
        NotificationActions.getNotificationFactors(notificationDispatch)
        NotificationActions.getTypesOfDiagnosis(notificationDispatch)
        NotificationActions.getSentinelEvent(notificationDispatch)
        NotificationActions.getNeverEvent(notificationDispatch)
        NotificationActions.getNotificationById(notificationDispatch, firstNotificationId)
        NotificationActions.getLondonProtocolByNotificationId(notificationDispatch, firstNotificationId)
    }, [groupData, notificationDispatch])

    useEffect(() => {
        if (variableSelector.variablesList) {
            const varible = getVarible(variableSelector.variablesList, 'containerType', 'container')
            if (varible?.children) {
                const omsChoices = getVarible(varible?.children, 'name', 'u_oms_classification')
                setOmsClassifications(omsChoices?.choices || [])
            }
        }
    }, [variableSelector])

    useEffect(() => {
        if (notificationSelector.typesOfDiagnosis) {
            const typesOfDiagnosisFlat = notificationSelector.typesOfDiagnosis[0]?.result.flat()
            const td = typesOfDiagnosisFlat
                ?.map((item: any) => ({
                    label: item.description,
                    value: item.sys_id
                }))
                .sort((o1: any, o2: any) => o1.label?.localeCompare(o2.label))
            setTypesOfDiagnosis(td || [])
        }
    }, [notificationSelector.typesOfDiagnosis])

    useEffect(() => {
        const londonProtocol = notificationSelector?.londonProtocol
        if (!londonProtocol) return
        setStartedDataV3({ setValue, data: londonProtocol })
    }, [notificationSelector?.londonProtocol, setValue])

    const getTreatmentNumber = useCallback(() => {
        if (notificationSelector?.notificationById) {
            if (notificationSelector?.notificationById.document_type?.value === 'atendimento') {
                if (protocoloLondon?.u_patient_care_number) {
                    return protocoloLondon?.u_patient_care_number
                }
                return notificationSelector?.notificationById?.document_number?.value
            }
        }
        return undefined
    }, [notificationSelector?.notificationById, protocoloLondon?.u_patient_care_number])

    const getSentinelEvent = useMemo(() => {
        if (notificationSelector?.sentinelEvent && notification) {
            const notificationVersion = notification?.u_version?.value || process.env.REACT_APP_NOTIFICATION_VERSION
            const sentinelEventValue = notificationSelector.notificationById?.u_sentinel_event_jci?.value
            const [sentinelEvent] = notificationSelector?.sentinelEvent
                ?.filter((se: any) => se?.u_version === notificationVersion)
                .filter((se: any) => se?.sys_id == sentinelEventValue)
            return sentinelEvent?.description || '-'
        }

        return '-'
    }, [notificationSelector?.sentinelEvent, notification, notificationSelector?.notificationById])

    const getNeverEvent = useMemo(() => {
        if (notificationSelector?.neverEvents && notification) {
            const notificationVersion = notification?.u_version?.value || process.env.REACT_APP_NOTIFICATION_VERSION
            const neverEventsReference = notificationSelector.notificationById?.u_never_events_reference?.value
            const [neverEvent] = notificationSelector?.neverEvents
                ?.filter((se: any) => se?.u_version === notificationVersion)
                .filter((se: any) => se?.sys_id == neverEventsReference)
            return neverEvent?.u_description || '-'
        }
        return '-'
    }, [notificationSelector?.neverEvents, notification, notificationSelector?.notificationById])

    const onChangeHelper = () => {
        setManualDirty(true)
    }

    const backToGroupTreatment = useCallback(() => {
        navigate(`/notificacoes/group-treatment/${id}`, { state: { currentStep: 3 } })
    }, [id, navigate])

    const saveGroupLondonProtocol = async (data: any) => {
        if (!groupData?.notifications?.length) return

        const notificationVersion = notificationSelector?.notificationById?.u_version?.value;
        const londonProtocolVersion = notificationVersion < LONDON_PROTOCOL_VERSION ? notificationVersion : LONDON_PROTOCOL_VERSION

        setIsSaving(true)
        try {
            const basePayload = {
                ...notificationSelector?.londonProtocol,
                ...data,
                do_not_perform_optional: false,
                u_version: londonProtocolVersion
            }
            delete (basePayload as any).sys_id

            const uNotUser = auth?.dataJwt?.sys_id || groupData.group_info?.u_notifica_user?.value || ''

            const buildRecord = (notifId: string, protocolSysId?: string) => ({
                ...basePayload,
                hospital_incident: notifId,
                u_notifica_user: uNotUser,
                ...(protocolSysId ? { sys_id: protocolSysId } : {})
            })

            const idByNotif = mergeFirstLondonProtocolIdFromRedux(
                groupData,
                await buildLondonProtocolSysIdByNotificationMap(groupData),
                notificationSelector.londonProtocol
            )
            const { withProtocol, withoutProtocol } = partitionNotificationsByProtocolMap(groupData, idByNotif)

            if (withProtocol.length > 0) {
                const updatePayload = {
                    records: withProtocol.map((n) => {
                        const notifId = getGroupNotificationSysId(n)
                        return buildRecord(notifId, idByNotif.get(notifId)!)
                    }),
                    options: { stopOnError: false as const }
                }
                await NotificationActions.updateLondonProtocolBatch(notificationDispatch, updatePayload)
            }
            if (withoutProtocol.length > 0) {
                const createPayload = {
                    records: withoutProtocol.map((n) => buildRecord(getGroupNotificationSysId(n))),
                    options: { stopOnError: false as const }
                }
                await NotificationActions.createLondonProtocolBatch(notificationDispatch, createPayload)
            }

            navigate(`/notificacoes/group-treatment/${id}`, {
                state: {
                    currentStep: 3,
                    messageSuccess: 'Protocolo de Londres em massa salvo com sucesso!'
                }
            })
        } catch (error) {
            console.error('Erro ao salvar Protocolo de Londres em grupo:', error)
        } finally {
            setIsSaving(false)
        }
    }

    const formIsDirty = !!Object.keys(dirtyFields).length || manualDirty

    return (
        <>
            {formIsDirty && (
                <Beforeunload onBeforeunload={() => 'Você fez edições que não forma salvas.'} />
            )}
            <WarningModal
                icon={<AiOutlineExclamationCircle size={50} className="text-yellow" />}
                customStyle={{ titleStyle: 'text-yellow' }}
                title="Edição não salva"
                subTitle="Você fez edições que não foram salvas. Se sair, você perderá as edições."
                leftButtonAction={confirmNavigation as () => void}
                rightButtonAction={cancelNavigation as () => void}
                isOpen={showPrompt as boolean}
                handleCloseModal={cancelNavigation as () => void}
                leftButtonText="Sair"
                rightButtonText="Continuar edição"
            />
            <DefaultModal
                isOpen={showModal}
                handleCloseModal={() => setShowModal(false)}
                children={
                    <DataLondonProtocolInfoModal
                        close={() => {
                            setShowModal(false)
                        }}
                    />
                }
            />

            <ContainerForm data-testid="test-group-london-form">
                <MainForm onSubmit={handleSubmit(saveGroupLondonProtocol)}>
                    <div className="flex items-baseline mt-8 justify-between">
                        <Breadcrumbs>
                            <Link className="mr-1 cursor-pointer" to="/notificacoes">
                                Gestão das notificações
                            </Link>
                            {' > '}
                            <span className="mr-1 cursor-pointer" onClick={backToGroupTreatment}>
                                Tratamento em Grupo
                            </span>
                            {' > '}
                            <SecondTextBreadcrumbs>Protocolo de Londres</SecondTextBreadcrumbs>
                        </Breadcrumbs>
                        <TopInputs>
                            <BackButton type="button" onClick={backToGroupTreatment}>
                                Voltar
                            </BackButton>
                            <SaveButton
                                type="submit"
                                disabled={!formIsDirty || isSaving}
                                className={!formIsDirty ? 'bg-disabled' : ''}
                            >
                                Salvar
                            </SaveButton>
                        </TopInputs>
                    </div>

                    <fieldset disabled={false}>
                        <div className="overflow-y-auto">
                            <Top>
                                <div>
                                    <TitleForm>Editar Protocolo de Londres em Massa</TitleForm>
                                    <SubtitleForm>
                                        Preencha os mesmos campos do protocolo individual; o conteúdo será aplicado a todas as
                                        notificações do grupo.
                                    </SubtitleForm>
                                </div>
                            </Top>
                            <hr />
                            <LondonProtocolFormFieldsBodyV3
                                register={register}
                                control={control}
                                setValue={setValue}
                                getValues={getValues}
                                errors={errors}
                                notification={notification}
                                notificationSelector={notificationSelector}
                                protocoloLondon={protocoloLondon}
                                protocoloNotificationFactors={protocoloNotificationFactors}
                                londonVaribles={londonVaribles}
                                typesOfDiagnosis={typesOfDiagnosis}
                                organizationalConsequences={organizationalConsequences}
                                chronologicalReportValue={chronologicalReportValue}
                                onChangeHelper={onChangeHelper}
                                notAllowed={false}
                                getTreatmentNumber={getTreatmentNumber}
                                neverEventDisplay={getNeverEvent}
                                sentinelEventDisplay={getSentinelEvent}
                                omsClassifications={omsClassifications}
                                setShowModal={setShowModal}
                                onlyProfilecanInputNSPOpinion={onlyProfilecanInputNSPOpinion}
                                context="group"
                                groupKeyDisplay={groupKeyDisplay}
                            />
                        </div>
                    </fieldset>
                </MainForm>
            </ContainerForm>
        </>
    )
}
